import React from 'react'
import {
  Box, List, ListItem, styled, Typography 
} from '@mui/material'

import { LinkAnimation } from './LinkAnimation'

type UseCasesProps = {
  data: {
    title: string
    icon_with_text_and_content_items: {
      title: string
      content: {
        children: {
          text: string
        }[]
      }[]
    }[]
    addlink: {
      cmnexternallink: boolean
      cmnlink: string
      cmnlinktext: string
    }
    show_divider: boolean
  }
}

const LineFlag = styled(Box)`
  ${({ theme }) => `
    background-color: ${theme.palette.primary.contrastText};
    border-radius: ${theme.spacing(1)};
    left: 0;
    position: absolute;
    top: 0;
  `}
`

const CircularImage = styled(Box)`
  ${({ theme }) => `
    width: 100%;
    height: 270px;
    background: url('/images/usecase-mo-bg.svg') no-repeat top left;
    background-size: cover;
    left: 0;
    margin-top: ${theme.spacing(20)};
    max-width: 100%;

    @media(min-width: 640px) {
      width: 290px;
      height: 290px;
      background: url('/images/use-case-bg.svg') no-repeat 82px 4px;
      background-size: cover;
      bottom: 0;
      left: auto;
      margin-top: 0;
      position: absolute;
      right: 0;
    }

    @media(min-width: 1024px) {
      width: 38%;
      height: calc(100% - 112px);
      background: url('/images/use-case-bg.svg') no-repeat 0 0;
      background-size: cover;
    }
  `}
`

export const UseCases = ({ data }: UseCasesProps) => (
  <Box
    sx={{
      position: 'relative',
      pt: { xs: 8, mobile: 11, laptop: 12 }
    }}
  >
    <Box
      sx={{
        maxWidth: 1152,
        mx: 'auto',
        pb: { mobile: 22, laptop: 15 },
        px: {
          xs: 3,
          mobile: 8,
          laptop: 4,
          desktop: 0
        }
      }}
    >
      <Box sx={{ maxWidth: { laptop: '62%' } }}>
        <Typography
          variant='h1'
          component='h2'
          sx={{
            fontSize: { laptop: 54 },
            letterSpacing: '-0.04em'
          }}
        >
          {data.title}
        </Typography>

        <List
          disablePadding
          sx={{
            display: 'grid',
            gap: { xs: 2, laptop: 4 },
            mt: { xs: 5.5, mobile: 4, laptop: 6.5 }
          }}
        >
          {data.icon_with_text_and_content_items.map(item => (
            <ListItem
              key={item.title}
              disablePadding
              sx={{ display: 'block', pl: { xs: 3, mobile: 6.5 } }}
            >
              <Typography
                sx={{
                  color: 'primary.contrastText',
                  fontSize: { xs: 18, mobile: 20, laptop: 24 },
                  fontWeight: 700
                }}
              >
                {item.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 18, mobile: 14, laptop: 18 },
                  mt: { xs: 1, mobile: 0.5 }
                }}
              >
                {item.content[0].children[0].text}
              </Typography>

              <LineFlag
                sx={{
                  width: { xs: 2, mobile: 4 },
                  height: { xs: 27, mobile: 30, laptop: 36 }
                }}
              />
            </ListItem>
          ))}
        </List>

        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            ml: { xs: 3, mobile: 6.5 },
            mt: { xs: 4, mobile: 5.5, laptop: 6.5 }
          }}
        >
          <LinkAnimation
            link={data.addlink.cmnlink}
            text={data.addlink.cmnlinktext}
            externalLink={data.addlink.cmnexternallink}
            sx={{
              fontSize: { xs: 18, laptop: 24 },
              mr: { xs: 2, mobile: 2.5 }
            }}
          />
        </Box>
      </Box>
    </Box>

    <CircularImage />

    {data.show_divider && <Box className='section-divider' />}
  </Box>
)
